import { Add } from "@mui/icons-material";
import { Button } from "@mui/material";
import React, { useCallback, useMemo, useState } from "react";
import { useSurvey } from "../../../../common/hooks/api/useSurvey";
import { RemoveVendorDialog } from "../../../components/dialog/RemoveVendorDialog";
import { GetCompliantPageWrapper } from "../../../components/layout/GetCompliantPageWrapper";
import { PageHeader } from "../../../components/layout/header/Header";
import {
  useDataRecipientsCatalog,
  useOrganizationVendors,
  usePrimaryOrganizationId,
} from "../../../hooks";
import { GetCompliantFooter, ProgressFooterSaveNotification } from "../ProgressFooter";
import { CustomVendorDialog, CustomVendorFormValues } from "./AddDataRecipients";
import { useDataStorageLocations } from "../../../util/resources/vendorUtils";
import {
  UpdateVendorParams,
  useAddCustomVendor,
  useUpdateVendor,
} from "../../../hooks/get-compliant/vendors/useUpdateVendor";
import {
  SurveyQuestion,
  SurveyQuestionProps,
} from "../../../components/get-compliant/survey/SurveyQuestion";
import { StorageLocationsTable } from "../../../components/organisms/data-recipients/StorageLocationsTable";
import { getVendorSettingsObject, DATA_STORAGE_LOCATION_CATEGORY } from "../../../util/vendors";
import { OrganizationDataRecipientDto } from "../../../../common/service/server/dto/OrganizationDataRecipientDto";
import { QuestionHeading } from "../../../components/vendor/QuestionHeading";

const DATA_STORAGE_SURVEY_NAME = "data-storage-survey";

const DATA_STORAGE_CATEGORIES = new Set([DATA_STORAGE_LOCATION_CATEGORY]);

const ADDITIONAL_STORAGE_QUESTION: SurveyQuestionProps = {
  type: "boolean",
  slug: "store-data-internal-systems",
  visible: true,
  question:
    "Does your business store consumer personal data in any internal systems -- i.e., storage locations that are not hosted by any of your vendors in the previous step?",
  helpText: "For example self-managed applications or databases which store consumer data.",
};

export const InternalStoragePage = (): React.ReactElement => {
  // Variables
  const organizationId = usePrimaryOrganizationId();
  const [addingVendor, setAddingVendor] = useState(false);
  const [confirmRemoveVendor, setConfirmRemoveVendor] = useState(null);
  const [editVendor, setEditVendor] = useState(null);

  const {
    answers,
    setAnswer,
    request: surveyRequest,
  } = useSurvey(organizationId, DATA_STORAGE_SURVEY_NAME);

  const [orgVendors, orgVendorsRequest, refreshOrgVendors, invalidateOrgVendors] =
    useOrganizationVendors(organizationId, [], true, undefined, ["CONSUMER"]);

  const [storageLocations, regularRecipients] = useDataStorageLocations(orgVendors);
  const [vendors, vendorsRequest, refreshCatalog] = useDataRecipientsCatalog(organizationId);
  const { updateVendor } = useUpdateVendor(organizationId, regularRecipients);

  const handleRefresh = useCallback(
    () => Promise.all([refreshOrgVendors(), refreshCatalog()]),
    [refreshOrgVendors, refreshCatalog],
  );

  const [handleAddCustomVendor, addCustomVendorRequest] = useAddCustomVendor(
    organizationId,
    handleRefresh,
    ["CONSUMER"],
  );

  const enabledVendors = useMemo(
    () => regularRecipients.map((dr) => vendors.find((v) => v.id === dr.vendorId)).filter(Boolean),
    [vendors, regularRecipients],
  );

  const updateVendorAndInvalidate = async (
    params: UpdateVendorParams,
  ): Promise<OrganizationDataRecipientDto> => {
    const updated = await updateVendor(params);
    invalidateOrgVendors();
    return updated;
  };

  const answeredAdditional = Boolean(answers[ADDITIONAL_STORAGE_QUESTION.slug]);
  const hasAdditional =
    answers[ADDITIONAL_STORAGE_QUESTION.slug] === "true" || storageLocations.length > 0;
  const needsAdditional = hasAdditional && storageLocations.length < 1;
  const nextDisabled = !answeredAdditional || needsAdditional;

  return (
    <GetCompliantPageWrapper
      progressFooter={
        <GetCompliantFooter
          presentStep="DataRecipients.InternalStorage"
          nextDisabled={nextDisabled}
          nextDisabledTooltip={"Please complete recipients before proceeding."}
          content={<ProgressFooterSaveNotification request={addCustomVendorRequest} />}
        />
      }
      requests={[vendorsRequest, orgVendorsRequest, surveyRequest]}
    >
      <PageHeader titleContent="Internal Storage" />

      <SurveyQuestion
        question={{
          ...ADDITIONAL_STORAGE_QUESTION,
          isDisabled: storageLocations.length > 0,
          disabledReason: "You have at least one storage location.",
        }}
        answer={storageLocations.length > 0 ? "true" : answers[ADDITIONAL_STORAGE_QUESTION.slug]}
        onAnswer={(ans) =>
          setAnswer(
            ADDITIONAL_STORAGE_QUESTION.slug,
            ans,
            ADDITIONAL_STORAGE_QUESTION.question as string,
          )
        }
      />

      {hasAdditional && (
        <div className="mt-xl">
          <div className="mb-lg">
            <QuestionHeading
              condensed
              question="Name your additional data storage locations below. Use a name your team will understand."
            />
          </div>
          <div className="mb-md">
            <StorageLocationsTable
              storageLocations={storageLocations}
              onEdit={(storageLocation) => setEditVendor(storageLocation)}
              onRemove={(storageLocation) =>
                setConfirmRemoveVendor(
                  orgVendors.find((v) => v.vendorId === storageLocation.vendorId),
                )
              }
              className="mt-lg"
            />
          </div>

          <Button onClick={() => setAddingVendor(true)} startIcon={<Add />} color="secondary">
            Add
          </Button>
        </div>
      )}

      {/*Add Vendor Dialog*/}
      <CustomVendorDialog
        open={addingVendor}
        existingCategories={DATA_STORAGE_CATEGORIES}
        showCategories={false}
        includeUrl={false}
        defaultCategory={DATA_STORAGE_LOCATION_CATEGORY}
        header={"Add Data Storage Location"}
        confirmLabel={"Add"}
        onClose={() => setAddingVendor(false)}
        onDone={async (values) => {
          await handleAddCustomVendor({
            ...values,
            dataAccessibility: "ACCESSIBLE",
            dataRecipientType: "storage_location",
            completed: true,
          });
          setAddingVendor(false);
        }}
        enabledVendors={enabledVendors}
      />
      {/*Edit Vendor Dialog*/}
      <CustomVendorDialog
        open={!!editVendor}
        onClose={() => setEditVendor(null)}
        onDone={async (values: CustomVendorFormValues) => {
          const newValues = Object.assign(getVendorSettingsObject(editVendor), values);
          await updateVendorAndInvalidate(newValues as UpdateVendorParams);
          setEditVendor(null);
        }}
        enabledVendors={enabledVendors}
        header={"Edit Data Storage Location"}
        existingCategories={DATA_STORAGE_CATEGORIES}
        showCategories={false}
        includeUrl={false}
        vendor={editVendor}
      />
      <RemoveVendorDialog
        open={!!confirmRemoveVendor}
        onClose={() => setConfirmRemoveVendor(null)}
        onAfterRemoved={handleRefresh}
        vendorId={confirmRemoveVendor?.vendorId}
        vendorName={confirmRemoveVendor?.name}
        collectionContext={["CONSUMER"]}
      />
    </GetCompliantPageWrapper>
  );
};
